import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'GREENSMART\u00A0HOME', path: '/' },
    { name: 'CUBi', path: '/cubi' },
    { name: 'BLOCKSMART', path: '/blocksmart' },
    { name: 'INVESTORS', path: '/investor' },
    { name: 'DISTRESSED\u00A0ASSETS', path: '/cubi/stalled-development' },
    { name: 'ABOUT US', path: '/about' },
    { name: 'BLOG', path: '/blog' },
    { name: 'CONTACT', path: '/contact' },
  ];

  return (
    <>
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div data-testid="logo-link" className="flex items-center group cursor-pointer -ml-4" onClick={(e) => { e.preventDefault(); setShowVideo(true); }}>
            <video 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/greensmart-logo-video.mp4`}
              poster="https://customer-assets.emergentagent.com/job_hvac-renewables/artifacts/ixlsz35t_001%20GS%20logo.png"
              autoPlay 
              loop 
              muted 
              playsInline
              preload="auto"
              style={{height: '80px', width: 'auto', borderRadius: '8px', objectFit: 'contain', display: 'block'}}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              link.path.startsWith('http') ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className="px-4 py-2 text-sm font-mono tracking-wider transition-colors text-muted-foreground hover:text-foreground"
                >
                  {link.name}
                </a>
              ) : (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 text-sm font-mono tracking-wider transition-colors relative group ${
                  location.pathname === link.path ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
              )
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/portal"
              data-testid="portal-login-btn"
              className="px-6 py-2 border border-primary/50 text-primary text-sm font-mono uppercase tracking-wider hover:bg-primary/10 transition-colors"
            >
              PORTAL
            </Link>
            <a
              href="tel:+17787292202"
              data-testid="call-now-btn"
              className="px-6 py-2 bg-primary text-black text-sm font-mono uppercase tracking-wider hover:bg-primary/90 transition-colors font-bold"
            >
              CALL NOW
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-foreground p-2"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              data-testid="mobile-menu"
              className="lg:hidden overflow-hidden border-t border-white/10 bg-background"
            >
              <div className="py-4 space-y-2">
                {navLinks.map((link) => (
                  link.path.startsWith('http') ? (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                      className="block px-4 py-3 text-sm font-mono tracking-wider transition-colors text-muted-foreground hover:text-foreground hover:bg-white/5"
                    >
                      {link.name}
                    </a>
                  ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`block px-4 py-3 text-sm font-mono tracking-wider transition-colors ${
                      location.pathname === link.path ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                  )
                ))}
                <div className="px-4 pt-4 space-y-2">
                  <Link
                    to="/portal"
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-portal-btn"
                    className="block w-full px-6 py-2 border border-primary/50 text-primary text-sm font-mono uppercase tracking-wider hover:bg-primary/10 transition-colors text-center"
                  >
                    PORTAL
                  </Link>
                  <a
                    href="tel:+17787292202"
                    data-testid="mobile-call-btn"
                    className="block w-full px-6 py-2 bg-primary text-black text-sm font-mono uppercase tracking-wider hover:bg-primary/90 transition-colors text-center font-bold"
                  >
                    CALL NOW
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
    {showVideo && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
        <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setShowVideo(false)} className="absolute -top-10 right-0 text-white/70 hover:text-white text-2xl font-bold">✕</button>
          <video 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/greensmart-logo-video.mp4`}
            autoPlay loop muted playsInline controls
            className="w-full rounded-xl shadow-2xl"
          />
        </div>
      </div>
    )}
    </>
  );
};
